<script lang="ts">
  // Top bar (components doc §1 "Top bar" / "Selection bar"): back, folders (mobile), search,
  // status chips, reader navigation, list actions, the read-mode split/full buttons (ui.js 1.5-3)
  // and the selection bar with its menu.
  import { getLang, t } from '$lib/i18n';
  import { isSnoozedFolder } from '$lib/mail/snooze';
  import { store } from '$lib/store';
  import type { Screen, ThreadRow } from '$lib/types';
  import { anchorFromElement, tooltip, type MenuAnchor } from '$lib/util/dom';
  import Icon from '../ui/Icon.svelte';
  import Menu from '../ui/Menu.svelte';
  import { addLabelFlow, canArchiveIn, deleteUids, emptyFolderFlow, isInJunk, markSeen, rowUids, selectionActive, toggleJunk, toggleStar } from './_local';
  import { readModeLabels, type ReadMode } from './readMode';
  import SearchBox from './SearchBox.svelte';

  let {
    screen,
    split,
    readMode,
    ontogglereadmode,
  }: {
    screen: Screen;
    split: boolean;
    readMode: ReadMode;
    ontogglereadmode: () => void;
  } = $props();

  let search = $state<SearchBox | null>(null);
  let selMenuAnchor = $state<MenuAnchor | null>(null);
  let selMenuFromCtx = $state(false);
  let selMoreBtn = $state<HTMLButtonElement | null>(null);

  const isRead = $derived(screen.name === 'read');
  const isList = $derived(screen.name === 'list');
  const showSearch = $derived(isList || split);
  const selbar = $derived((isList || split) && selectionActive());
  const selCount = $derived(store.selection.size);
  const pos = $derived(isRead ? store.readerPos() : null);
  const canArchive = $derived(canArchiveIn());
  const inSnoozed = $derived(isSnoozedFolder(store.currentFolder));
  const labels = $derived(readModeLabels(getLang()));
  const selUids = $derived([...store.selection]);

  export function focusSearch(): void {
    search?.focus();
  }
  export function searchElement(): HTMLInputElement | null {
    return search?.element() ?? null;
  }
  /** Row context menu (spec §9): select the row if needed, then open the selection menu there. */
  export function openContextMenu(ev: MouseEvent, row: ThreadRow): void {
    const uids = rowUids(row);
    if (!uids.every((u) => store.selection.has(u))) store.selectOnly(row.uid);
    selMenuFromCtx = true;
    selMenuAnchor = { x: ev.clientX, y: ev.clientY, above: ev.clientY };
  }

  // Close the menu when the selection empties.
  $effect(() => {
    if (store.selection.size === 0) selMenuAnchor = null;
  });

  function openSelMenu() {
    selMenuFromCtx = false;
    selMenuAnchor = selMoreBtn ? anchorFromElement(selMoreBtn) : null;
  }
  function closeMenu() {
    selMenuAnchor = null;
    selMenuFromCtx = false;
  }
  function run(fn: () => unknown) {
    closeMenu();
    void fn();
  }
  function selAll() {
    store.selectAllShown();
  }
</script>

{#if selbar}
  <header class="top selbar">
    <button type="button" class="ghost" aria-label={t('cancel')} use:tooltip onclick={() => store.clearSelection()}><Icon name="x" size={20} /></button>
    <h2>{t('selectedN', { n: selCount })}</h2>
    <span class="grow"></span>
    {#if canArchive}<button type="button" class="ghost" aria-label="{t('archive')} (E)" use:tooltip onclick={() => void store.archive(selUids)}><Icon name="archive" size={18} /></button>{/if}
    <button type="button" class="ghost danger-h" aria-label="{t('del')} (#)" use:tooltip onclick={() => void deleteUids(selUids).then(() => store.clearSelection())}><Icon name="trash" size={18} /></button>
    <button type="button" class="ghost" aria-label={t('markUnread')} use:tooltip onclick={() => void store.bulk('unseen')}><Icon name="inbox" size={18} /></button>
    <span class="selmore-wrap">
      <button type="button" class="ghost selmore" aria-label={t('more')} aria-expanded={!!selMenuAnchor} aria-haspopup="menu" use:tooltip bind:this={selMoreBtn} onclick={openSelMenu}><Icon name="more" size={18} /></button>
      {#if selMenuAnchor}
        <Menu anchor={selMenuAnchor} label={t('more')} onclose={closeMenu}>
          {#if selMenuFromCtx}
            {#if canArchive}<button type="button" role="menuitem" onclick={() => run(() => store.archive(selUids))}><Icon name="archive" size={16} /> {t('archive')}</button>{/if}
            <button type="button" role="menuitem" onclick={() => run(() => store.bulk('unseen'))}><Icon name="inbox" size={16} /> {t('markUnread')}</button>
            <button type="button" role="menuitem" class="ctx-del" onclick={() => run(() => deleteUids(selUids).then(() => store.clearSelection()))}><Icon name="trash" size={16} /> {t('del')}</button>
            <div class="menu-sep"></div>
          {/if}
          <button type="button" role="menuitem" onclick={() => run(() => toggleStar(selUids).then(() => store.clearSelection()))}><Icon name="star" size={16} /> {t('star')}</button>
          {#if inSnoozed}
            <button type="button" role="menuitem" class="unsnooze-btn" onclick={() => run(() => store.unsnooze(selUids))}><Icon name="inbox" size={16} /> {t('unsnooze')}</button>
          {:else}
            <button type="button" role="menuitem" class="snooze-btn" onclick={() => run(() => (store.snoozeTarget = selUids))}><Icon name="moon" size={16} /> {t('snooze')}</button>
          {/if}
          <button type="button" role="menuitem" class="mutebtn" onclick={() => run(() => store.muteSelected())}><Icon name="moon" size={16} /> {t('muteThread')}</button>
          <div class="menu-sep"></div>
          <button type="button" role="menuitem" onclick={() => run(() => (store.moveSheet = true))}><Icon name="move" size={16} /> {t('move')}</button>
          <button type="button" role="menuitem" onclick={() => run(() => addLabelFlow(selUids).then(() => store.clearSelection()))}><Icon name="tag" size={16} /> {t('addLabel')}</button>
          <button type="button" role="menuitem" onclick={() => run(() => toggleJunk(selUids))}><Icon name="junk" size={16} /> {isInJunk() ? t('notJunk') : t('junk')}</button>
        </Menu>
      {/if}
    </span>
  </header>
{:else}
  <header class="top">
    {#if isRead && !split}
      <button type="button" class="ghost" aria-label={t('back')} use:tooltip onclick={() => store.backToList()}><span class="flip-ltr"><Icon name="back" size={22} /></span></button>
    {:else}
      <button type="button" class="ghost mobile-only" aria-label={t('folders')} use:tooltip onclick={() => (store.foldersSheet = true)}><Icon name="folder" size={20} /></button>
    {/if}
    {#if readMode === 'full' && split}
      <button type="button" class="tik-topbtn tik-backbtn" aria-label={labels.back} title={labels.back} use:tooltip onclick={() => store.backToList()}>
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" class="flip-ltr"><path d="M5 12h14" /><path d="m12 5 7 7-7 7" /></svg>
      </button>
    {/if}
    {#if showSearch}
      <SearchBox bind:this={search} />
    {/if}
    <span class="grow"></span>
    {#if store.syncing}<span class="spin" aria-label={t('loading')}></span>{/if}
    {#if store.offline}
      <span class="off">{t('offline')}</span>
    {:else if store.liveDown}
      <span class="off live-down" use:tooltip={t('liveDownHint')}>{t('liveDown')}</span>
    {/if}
    {#if isRead && pos}
      <span class="rnav">
        <button type="button" class="ghost" disabled={pos.i === 0} aria-label={t('prevMsg')} use:tooltip onclick={() => void store.openNeighbor(-1)}><span class="flip-ltr"><Icon name="chev" size={18} /></span></button>
        <span class="rpos" dir="ltr">{pos.i + 1}/{pos.total}</span>
        <button type="button" class="ghost" disabled={pos.i + 1 >= pos.total} aria-label={t('nextMsg')} use:tooltip onclick={() => void store.openNeighbor(1)}><span class="flip-ltr rnav-next"><Icon name="chev" size={18} /></span></button>
      </span>
    {/if}
    {#if isList}
      <button type="button" class="ghost" aria-label={t('selectAll')} use:tooltip onclick={selAll}><Icon name="check" size={18} /></button>
      <button type="button" class="ghost" aria-label={t('markAllRead')} use:tooltip onclick={() => void store.markFolderRead()}><Icon name="inbox" size={18} /></button>
      {#if store.canEmpty()}
        <button type="button" class="ghost danger-h" aria-label={t('emptyNow')} use:tooltip onclick={() => void emptyFolderFlow()}><Icon name="eraser" size={18} /></button>
      {/if}
      <button type="button" class="ghost" aria-label={t('viewSort')} use:tooltip onclick={() => (store.viewSheet = true)}><Icon name="sort" size={18} /></button>
    {/if}
    <button type="button" class="ghost" aria-label={t('refresh')} use:tooltip onclick={() => void store.refresh()}><Icon name="refresh" size={18} /></button>
    {#if isList || isRead}
      <button
        type="button"
        class="tik-topbtn tik-modebtn"
        data-mode={readMode}
        aria-pressed={readMode === 'full'}
        aria-label={readMode === 'full' ? labels.toSplit : labels.toFull}
        title={readMode === 'full' ? labels.toSplit : labels.toFull}
        use:tooltip
        onclick={ontogglereadmode}
      >
        {#if readMode === 'full'}
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M10 4v16" /></svg>
        {:else}
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="4" width="18" height="16" rx="2" /></svg>
        {/if}
      </button>
    {/if}
  </header>
{/if}

<style>
  /* @css shell/TopBar start */
  .selmore-wrap {
    position: relative;
    display: inline-flex
  }

  .menu-sep {
    height: 1px;
    background: var(--border-subtle);
    margin: var(--space-1) 0
  }

  .top {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    padding-inline: var(--space-3);
    height: 56px;
    background: var(--surface-card);
    border-bottom: 1px solid var(--border-subtle)
  }

  .grow {
    flex: 1
  }

  .ghost {
    padding: var(--space-2);
    border-radius: var(--radius-control);
    color: var(--text-muted)
  }

  .ghost:hover {
    background: var(--surface-sunken);
    color: var(--text-strong)
  }

  .spin {
    width: 16px;
    height: 16px;
    border: 2px solid var(--border-default);
    border-top-color: var(--blue-500);
    border-radius: var(--radius-pill);
    animation: svelte-1n46o8q-rot .8s linear infinite
  }

  .off {
    font-size: var(--text-xs);
    color: var(--warning-500)
  }

  .selbar {
    background: var(--blue-50)
  }

  :global(html.tik-dark) .selbar {
    background: #4a7cf624
  }

  .selbar h2 {
    color: var(--blue-700);
    font-size: var(--text-base)
  }

  :global(html.tik-dark) .selbar h2 {
    color: var(--blue-200)
  }

  .danger-h:hover {
    color: var(--danger-500)
  }

  .rnav {
    display: inline-flex;
    align-items: center;
    gap: var(--space-1)
  }

  .rpos {
    color: var(--text-muted);
    font-size: var(--text-xs);
    font-variant-numeric: tabular-nums;
    min-width: 44px;
    text-align: center
  }

  .rnav-next {
    transform: rotate(180deg)
  }

  .rnav .ghost:disabled {
    opacity: .35;
    cursor: default
  }

  .ghost:active {
    background: var(--mail-pressed)
  }

  .mobile-only {
    display: none
  }

  @media (max-width:900px) {
  button.mobile-only {
      display: inline-grid
    }
  }

  .top {
    height: auto;
    min-height: 56px;
    padding-top: env(safe-area-inset-top)
  }

  @media (max-width:480px) {
  .top {
      flex-wrap: wrap;
      height: auto;
      min-height: 56px;
      padding-top: calc(var(--space-2) + env(safe-area-inset-top));
      padding-bottom: var(--space-2);
      row-gap: var(--space-2)
    }
  }

  .tik-topbtn {
    display: grid;
    place-items: center;
    flex: none;
    inline-size: 34px;
    block-size: 34px;
    padding: 0;
    border: none;
    border-radius: var(--radius-control);
    background: none;
    color: var(--text-muted);
    font: inherit;
    cursor: pointer
  }

  .tik-topbtn:hover {
    background: var(--surface-sunken);
    color: var(--text-strong)
  }

  .tik-topbtn:active {
    background: var(--mail-pressed)
  }

  .tik-topbtn[aria-pressed="true"] {
    color: var(--text-link)
  }

  .tik-topbtn[hidden] {
    display: none
  }

  @media (max-width:900px) {
  .tik-topbtn {
      display: none
    }
  }

  @keyframes svelte-1n46o8q-rot {
    to {
      transform: rotate(360deg)
    }
  }
  /* @css shell/TopBar end */
</style>
